// UltraFlex - Day Pass Viewer (via email link or direct access)
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { MapPin, Calendar, AlertCircle, Loader, Dumbbell, Check } from 'lucide-react';
import { DayPassWalletButtons } from '../components/WalletButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DayPassView = () => {
  const { id } = useParams();
  const [dayPass, setDayPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) fetchDayPass();
  }, [id]);

  const fetchDayPass = async () => {
    try {
      const res = await fetch(`${API_URL}/api/day-pass/view/${id}`);
      if (!res.ok) throw new Error('Day pass not found');
      setDayPass(await res.json());
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <Loader className="w-10 h-10 text-[#e63946] animate-spin" />
      </div>
    );
  }

  if (error || !dayPass) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 text-lg">{error || 'Day pass not found'}</p>
          <Link to="/day-pass" className="inline-block mt-4 bg-[#e63946] text-white px-6 py-2 rounded-lg text-sm">Buy Day Pass</Link>
        </div>
      </div>
    );
  }

  if (dayPass.payment_status !== 'paid') {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
          <p className="text-yellow-400 text-lg">Payment not completed</p>
          <p className="text-gray-400 text-sm mt-2">This day pass has not been paid for yet.</p>
        </div>
      </div>
    );
  }

  const isToday = dayPass.visit_date === new Date().toISOString().split('T')[0];
  const isPast = new Date(dayPass.visit_date + 'T23:59:59') < new Date();

  return (
    <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center p-4" data-testid="day-pass-view">
      <div className="max-w-md w-full">
        {/* Day Pass Card */}
        <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-2xl border border-gray-700 overflow-hidden">
          <div className="bg-[#e63946] px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-white" />
              <span className="text-white font-bold text-sm">ULTRAFLEX DAY PASS</span>
            </div>
            {dayPass.checked_in && (
              <span className="flex items-center gap-1 text-white/90 text-xs">
                <Check className="w-4 h-4" /> Checked In
              </span>
            )}
          </div>

          <div className="p-6 space-y-4">
            {isPast && !isToday && (
              <div className="bg-gray-700/30 rounded-lg p-3 text-center">
                <p className="text-gray-400 text-sm">This pass has expired</p>
              </div>
            )}
            {isToday && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                <p className="text-green-400 text-sm font-semibold">Valid Today</p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-[#e63946]" />
              <div>
                <p className="text-xs text-gray-400">Gym Location</p>
                <p className="text-white font-semibold">{dayPass.site_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-[#e63946]" />
              <div>
                <p className="text-xs text-gray-400">Valid On</p>
                <p className="text-white font-semibold">
                  {new Date(dayPass.visit_date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>
            <div className="text-center text-gray-400 text-xs">{dayPass.first_name} {dayPass.last_name}</div>

            {dayPass.qr_token && !isPast && (
              <div className="bg-white rounded-xl p-6 mx-auto max-w-[220px]" data-testid="day-pass-qr">
                <QRCode value={dayPass.qr_token} size={180} level="H" />
              </div>
            )}
            {!isPast && <p className="text-center text-gray-400 text-xs">Show this QR code at the gym entrance</p>}
          </div>
        </div>

        {/* Wallet Pass Buttons */}
        {dayPass?.id && !isPast && (
          <div className="mt-4 bg-[#1a1a2e] rounded-xl border border-gray-700 p-4" data-testid="daypass-view-wallet">
            <DayPassWalletButtons retrievalCode={dayPass.id} />
          </div>
        )}

        <div className="mt-6 text-center space-y-3">
          <Link to="/" className="block text-gray-400 text-sm hover:text-white">Back to UltraFlex</Link>
          <Link to="/join" className="block text-[#e63946] text-sm font-semibold hover:text-red-400">Want full access? Become a member &rarr;</Link>
        </div>
      </div>
    </div>
  );
};

export default DayPassView;
